import React, { useEffect } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { motion, useScroll, useSpring } from "framer-motion";
import PageShell from "../components/PageShell";
import { POST_BY_SLUG, POSTS } from "../data/posts";

function formatDate(iso) {
  return new Date(iso).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

export default function BlogPost() {
  const { slug } = useParams();
  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, { stiffness: 100, damping: 30 });

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [slug]);

  const p = POST_BY_SLUG[slug];
  if (!p) return <Navigate to="/blog" replace />;

  const idx = POSTS.findIndex((x) => x.slug === slug);
  const next = POSTS[(idx + 1) % POSTS.length];

  return (
    <PageShell>
      {/* Reading progress bar */}
      <motion.div
        style={{ scaleX, transformOrigin: "0% 50%", background: "var(--text-primary)" }}
        className="fixed top-16 md:top-20 left-0 right-0 h-[2px] z-40"
      />

      <article className="pt-28 md:pt-36">
        <div className="max-w-3xl mx-auto px-6 md:px-10">
          <Link
            to="/blog"
            data-testid="post-back"
            className="inline-flex items-center gap-2 text-[11px] tracking-[0.25em] uppercase text-muted-x hover:text-primary-x transition-colors font-mono-x"
          >
            ← All writing
          </Link>

          <div className="mt-10 text-[11px] tracking-[0.25em] uppercase text-muted-x font-mono-x flex items-center gap-3 flex-wrap">
            <span>{p.tag}</span>
            <span className="h-px w-8 bg-hairline-strong" />
            <span>{formatDate(p.date)}</span>
            <span className="h-px w-8 bg-hairline-strong" />
            <span>{p.readTime} read</span>
          </div>

          <motion.h1
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            data-testid="post-title"
            className="mt-6 font-display text-5xl md:text-7xl font-bold tracking-[-0.04em] leading-[0.95] text-primary-x text-balance"
          >
            {p.title}
          </motion.h1>

          <div className="mt-10 flex items-center gap-4">
            <span
              className="h-12 w-12 rounded-full ring-1 ring-hairline-strong"
              style={{ background: "var(--surface-2)" }}
            />
            <div>
              <div className="text-primary-x text-sm">{p.author}</div>
              <div className="text-muted-x text-xs tracking-[0.18em] uppercase font-mono-x">
                {p.role}
              </div>
            </div>
          </div>
        </div>

        {/* Cover */}
        <div className="max-w-5xl mx-auto px-6 md:px-10 mt-16">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="relative aspect-[16/9] overflow-hidden rounded-2xl border border-hairline bg-surface"
          >
            <img
              src={p.cover}
              alt={p.title}
              className="absolute inset-0 w-full h-full object-cover"
            />
          </motion.div>
        </div>

        {/* Body */}
        <div className="max-w-3xl mx-auto px-6 md:px-10 py-20 md:py-28">
          <div className="prose prose-invert max-w-none">
            <p className="font-display text-2xl md:text-3xl leading-snug text-primary-x text-balance">
              {p.excerpt}
            </p>
            <div className="mt-10 space-y-7 text-secondary-x leading-relaxed text-lg">
              {p.paragraphs.map((para, i) => (
                <p key={i}>{para}</p>
              ))}
            </div>
          </div>

          {/* Share row */}
          <div className="mt-16 pt-8 border-t border-hairline flex items-center justify-between text-[11px] tracking-[0.22em] uppercase text-muted-x font-mono-x">
            <span>Share</span>
            <div className="flex items-center gap-3">
              <a className="hover:text-primary-x transition-colors" href="#" onClick={(e) => e.preventDefault()}>X</a>
              <a className="hover:text-primary-x transition-colors" href="#" onClick={(e) => e.preventDefault()}>LinkedIn</a>
              <a className="hover:text-primary-x transition-colors" href="#" onClick={(e) => e.preventDefault()}>Copy link</a>
            </div>
          </div>
        </div>

        {/* Next */}
        <section className="border-t border-hairline bg-surface">
          <Link
            to={`/blog/${next.slug}`}
            data-testid="post-next"
            className="group block max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28"
          >
            <div className="text-[11px] tracking-[0.25em] uppercase text-muted-x font-mono-x mb-4">
              Read next
            </div>
            <div className="flex items-end justify-between gap-6 flex-wrap">
              <h3 className="font-display text-4xl md:text-6xl font-bold tracking-[-0.03em] leading-tight text-primary-x max-w-3xl">
                {next.title}
              </h3>
              <span className="inline-flex items-center gap-3 text-primary-x text-sm">
                {next.tag}
                <span className="transition-transform group-hover:translate-x-1">→</span>
              </span>
            </div>
          </Link>
        </section>
      </article>
    </PageShell>
  );
}
